/**
 * Central, typed event catalog. Every product-analytics event Disciplio sends
 * must be declared here with its allowed property keys, so a call site can
 * never accidentally spread user-created content (task/goal text, reflections,
 * AI output, profile fields, tokens, ...) into an analytics payload.
 */

import { normalizeCampaignValue } from "./campaign";

export type AnalyticsEventName =
  | "registration_completed"
  | "login_completed"
  | "onboarding_completed"
  | "daily_task_created"
  | "daily_task_completed"
  | "daily_reflection_saved"
  | "ai_recommendation_generated"
  | "ai_recommendation_viewed"
  | "ai_recommendation_used"
  | "ai_recommendation_applied"
  | "ai_recommendation_dismissed"
  | "planning_suggestion_applied"
  | "goal_details_opened"
  | "goal_notes_saved"
  | "goal_mention_inserted"
  | "goal_mention_opened"
  | "coach_opened"
  | "coach_new_chat"
  | "coach_message_sent"
  | "coach_thinking_effort_changed"
  | "coach_voice_started"
  | "coach_voice_transcribed"
  | "coach_voice_failed"
  | "coach_response_first_token"
  | "coach_response_received"
  | "coach_response_failed"
  | "coach_response_retried"
  | "coach_history_opened"
  | "coach_conversation_deleted"
  | "coach_starter_prompt_used"
  | "memory_import_opened"
  | "memory_import_assistant_selected"
  | "memory_import_prompt_copied"
  | "memory_import_external_opened"
  | "memory_import_previewed"
  | "memory_import_applied"
  | "memory_import_cancelled";

/**
 * The complete allow-list of event property keys. Anything not listed here is
 * dropped before the event ever reaches PostHog. Values are restricted to
 * strings/booleans/numbers so a caller cannot smuggle a nested object
 * (e.g. a raw API response or domain entity) into an event.
 */
export type AnalyticsEventProperties = Partial<{
  page: string;
  source: string;
  platform: "web";
  surface: "app" | "landing";
  campaign_source: string;
  campaign_medium: string;
  campaign_name: string;
  campaign_content: string;
  recommendation_type: string;
  recommendation_status: "GENERATED" | "FALLBACK";
  language: string;
  scope: string;
  item_type: string;
  was_ai_generated: boolean;
  success: boolean;
  account_role: string;
  onboarding_completed: boolean;
  thinking_effort: string;
  status: string;
  step: string;
  assistant: string;
  launchMode: string;
  newCount: number;
  duplicateCount: number;
  conflictCount: number;
  rejectedCount: number;
  importedCount: number;
  replacedCount: number;
  keptBothCount: number;
  skippedCount: number;
}>;

const ALLOWED_PROPERTY_KEYS: ReadonlySet<keyof AnalyticsEventProperties> =
  new Set([
    "page",
    "source",
    "platform",
    "surface",
    "campaign_source",
    "campaign_medium",
    "campaign_name",
    "campaign_content",
    "recommendation_type",
    "recommendation_status",
    "language",
    "scope",
    "item_type",
    "was_ai_generated",
    "success",
    "account_role",
    "onboarding_completed",
    "thinking_effort",
    "status",
    "step",
    "assistant",
    "launchMode",
    "newCount",
    "duplicateCount",
    "conflictCount",
    "rejectedCount",
    "importedCount",
    "replacedCount",
    "keptBothCount",
    "skippedCount",
  ]);

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/**
 * Properties allowed on `posthog.identify()` calls. Deliberately a separate,
 * narrower allow-list from event properties: identification happens once per
 * session and must never carry profile text (motivation, interests,
 * challenge areas), email, or full name — only coarse account attributes.
 */
export type AnalyticsIdentifyProperties = Partial<{
  language: string;
  account_role: string;
  onboarding_completed: boolean;
  account_created_at: string;
  plan: string;
}>;

const ALLOWED_IDENTIFY_KEYS: ReadonlySet<keyof AnalyticsIdentifyProperties> =
  new Set([
    "language",
    "account_role",
    "onboarding_completed",
    "account_created_at",
    "plan",
  ]);

export function sanitizeIdentifyProperties(
  properties?: AnalyticsIdentifyProperties
): Record<string, string | number | boolean> {
  if (!properties) return {};
  const sanitized: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!ALLOWED_IDENTIFY_KEYS.has(key as keyof AnalyticsIdentifyProperties))
      continue;
    if (!isPrimitive(value)) continue;
    sanitized[key] = value;
  }
  return sanitized;
}

/**
 * Strips any property key that is not on the allow-list and any value that is
 * not a primitive. This is the single choke point that keeps arbitrary domain
 * objects, API responses, or free-text content out of analytics payloads.
 */
export function sanitizeEventProperties(
  properties?: AnalyticsEventProperties
): Record<string, string | number | boolean> {
  if (!properties) return {};
  const sanitized: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!ALLOWED_PROPERTY_KEYS.has(key as keyof AnalyticsEventProperties))
      continue;
    if (!isPrimitive(value)) continue;
    if (
      key === "campaign_source" ||
      key === "campaign_medium" ||
      key === "campaign_name" ||
      key === "campaign_content"
    ) {
      const normalized = normalizeCampaignValue(String(value));
      if (normalized) sanitized[key] = normalized;
      continue;
    }
    sanitized[key] = value;
  }
  return sanitized;
}
